if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (window.MF_DEBUG) console.debug('[mf] DISCONNECTED (gallery element removed/replaced)');
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }

      if (this.zoomInitHandler) {
        window.removeEventListener('on:debounced-resize', this.zoomInitHandler);
      }

      if (this.boundOnVariantChange && this.section) {
        this.section.removeEventListener('on:variant:change', this.boundOnVariantChange);
      }

      if (this.idObserver) this.idObserver.disconnect();
      if (this.variantIdInput && this.boundIdInputChange) {
        this.variantIdInput.removeEventListener('change', this.boundIdInputChange);
      }
    }

    init() {
      if (window.MF_DEBUG) console.debug('[mf] INIT', { metafieldActive: this.hasAttribute('data-metafield-active') });
      this.section = this.closest('.js-product');
      this.noSelectedVariant = this.hasAttribute('data-no-selected-variant');
      // Whether the server rendered the selected variant's metafield image set as
      // active on load (so a later switch to a plain variant restores native media).
      this.metafieldActive = this.hasAttribute('data-metafield-active');
      // Whether this product has any variant metafield images at all. When true,
      // this component controls gallery visibility instead of native grouping.
      this.hasMetafieldMedia = !!this.querySelector('[data-metafield-media]');
      // The variant ids belonging to THIS gallery's product. During combined-listing
      // product swaps the theme fires variant events for other products before this
      // gallery is replaced; acting on a foreign id would blank the correct render.
      this.ownedVariantIds = new Set(
        (this.dataset.ownedVariantIds || '').split(',').filter(Boolean)
      );
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.hasAttribute('data-zoom-enabled')) {
        this.galleryModal = this.querySelector('.js-media-zoom-template').content.firstElementChild.cloneNode(true);
      }

      // For metafield products this component owns visibility (applied below via
      // applyVariantMedia), so never run native grouping. Otherwise arrange the
      // native media as normal.
      if (this.mediaGroupingEnabled && !this.noSelectedVariant && !this.hasMetafieldMedia) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      if (this.zoomLinks) {
        if (this.dataset.zoomTrigger === 'hover') {
          this.triggerZoomInit();
        } else {
          this.zoomLinks.forEach((zoomLink) => {
            zoomLink.addEventListener('click', (evt) => {
              this.triggerZoomInit();
              evt.preventDefault();
            });
          });
        }
      }

      this.boundOnVariantChange = this.onVariantChange.bind(this);
      this.section.addEventListener('on:variant:change', this.boundOnVariantChange);

      // For products with metafield images, also watch the product form's id
      // input directly. The theme updates its `value` attribute on every committed
      // variant change (and the element persists across updates), so this is the
      // definitive selection signal — it re-applies once the selection settles,
      // self-correcting any transient flip caused by a late/racy variant event.
      if (this.hasMetafieldMedia) {
        this.variantIdInput = this.section.querySelector('.js-product-form [name="id"]')
          || this.section.querySelector('[name="id"]');
        if (this.variantIdInput) {
          this.lastAppliedVariantId = this.variantIdInput.value;
          this.idObserver = new MutationObserver(() => {
            const id = this.variantIdInput.value;
            if (window.MF_DEBUG) console.debug('[mf] id-attr-observer fired', { value: id, lastApplied: this.lastAppliedVariantId });
            if (id && id !== this.lastAppliedVariantId) this.applyVariantMedia();
          });
          this.idObserver.observe(this.variantIdInput, { attributes: true, attributeFilter: ['value'] });
          // Also react to the id input's own change event (dispatched on commit),
          // which catches value updates the attribute observer can miss.
          this.boundIdInputChange = () => {
            if (window.MF_DEBUG) console.debug('[mf] id-input change event', { value: this.variantIdInput.value });
            this.applyVariantMedia();
          };
          this.variantIdInput.addEventListener('change', this.boundIdInputChange);
        }

        if (window.MF_DEBUG && this.viewer) {
          // Trace whatever changes item visibility — the code path that reverts
          // the gallery to the default images.
          this._dbgViewerObserver = new MutationObserver((muts) => {
            muts.forEach((m) => {
              const el = m.target;
              if (!el.classList || !el.classList.contains('media-viewer__item')) return;
              const isNative = el.hasAttribute('data-native-media');
              const isMeta = el.classList.contains('media-viewer__item--metafield');
              if (isNative && el.style.display !== 'none') {
                console.debug('[mf] NATIVE item revealed', { id: el.dataset.mediaId, attr: m.attributeName, display: el.style.display });
                console.trace('[mf] native-revealed stack');
              }
              if (isMeta && !el.classList.contains('is-metafield-visible')) {
                console.debug('[mf] METAFIELD item hidden', { id: el.dataset.mediaId, attr: m.attributeName });
                console.trace('[mf] metafield-hidden stack');
              }
            });
          });
          this._dbgViewerObserver.observe(this.viewer, { attributes: true, attributeFilter: ['style', 'class'], subtree: true });

          // Catch the whole gallery/container being swapped out (childList),
          // which wouldn't show up as an item attribute change.
          const parent = this.parentElement;
          if (parent) {
            this._dbgParentObserver = new MutationObserver((muts) => {
              muts.forEach((m) => {
                if (m.type === 'childList' && (m.addedNodes.length || m.removedNodes.length)) {
                  console.debug('[mf] CONTAINER childList change', {
                    added: [...m.addedNodes].map((n) => n.nodeName),
                    removed: [...m.removedNodes].map((n) => n.nodeName),
                  });
                  console.trace('[mf] container-change stack');
                }
              });
            });
            this._dbgParentObserver.observe(parent, { childList: true, subtree: true });
          }
        }

        // Self-correct on every (re)render: a combined-listing swap can replace
        // this gallery with a server render whose "current variant" differs from
        // the committed selection (showing native). Applying from the id input
        // here fixes that immediately, without waiting for a variant event that
        // won't fire because the committed id didn't change.
        if (window.MF_DEBUG) console.debug('[mf] init self-apply', { metafieldActive: this.metafieldActive, idInputValue: this.variantIdInput && this.variantIdInput.value });
        this.applyVariantMedia();
      }
    }

    triggerZoomInit() {
      this.zoomInitHandler = this.zoomInitHandler || this.initZoom.bind(this);
      this.zoomEventListener = this.zoomEventListener || this.handleZoomMouseMove.bind(this);
      window.addEventListener('on:debounced-resize', this.zoomInitHandler);
      this.initZoom();
      this.zoomLinks.forEach((zoomLink) => {
        zoomLink.addEventListener('click', (evt) => {
          evt.preventDefault();
        });
      });
    }

    /**
     * Handle a change in variant on the page.
     * @param {Event} evt - variant change event dispatched by variant-picker
     */
    onVariantChange(evt) {
      const { variant } = evt.detail;
      if (window.MF_DEBUG) console.debug('[mf] on:variant:change', { eventVariantId: variant && variant.id, idInputValue: this.variantIdInput && this.variantIdInput.value });

      // For products with variant metafield images (custom.varianten_bilder),
      // this component is the single source of truth for gallery visibility:
      // show the selected variant's metafield set if it exists, otherwise the
      // full native (parent) gallery. Never delegate to native media grouping,
      // whose "no group" fallback would reveal every item at once.
      if (this.hasMetafieldMedia) {
        this.applyVariantMedia(variant);
        return;
      }

      // Products without any metafield images keep the original theme behaviour.
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (variant && variant.featured_media) {
        const variantMedia = this.viewer.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
        if (variantMedia) this.customSetActiveMedia(variantMedia, true);
      }
    }

    /**
     * Sets the gallery to the selected variant's metafield images, or to the full
     * native (parent) gallery when the variant has none. Uses display toggling so
     * the existing slider/counter (which count items by clientWidth) stay in sync.
     * @param {?object} variant - The selected variant object (may be null).
     */
    applyVariantMedia(variant) {
      // Resolve the current variant id from the product form's id input, which the
      // theme keeps in sync with the actual selection. This is the source of truth:
      // the on:variant:change event's variant can arrive stale or out of order
      // (fetch + option-selection timers, re-entrant events), which previously
      // reverted the gallery to native a moment after the correct update. Fall back
      // to the event's variant, and if the id is unknown, leave the gallery as-is.
      const idInput = this.variantIdInput
        || this.section.querySelector('.js-product-form [name="id"]')
        || this.section.querySelector('[name="id"]');
      let variantId = idInput && idInput.value ? String(idInput.value) : null;
      if (!variantId && variant && variant.id) variantId = String(variant.id);

      const owned = !variantId || !this.ownedVariantIds.size || this.ownedVariantIds.has(variantId);
      const metafieldMatch = !!variantId && !!this.viewer.querySelector(
        `.media-viewer__item[data-metafield-media][data-variant-id="${variantId}"]`
      );
      if (window.MF_DEBUG) {
        console.debug('[mf] applyVariantMedia', {
          idInputValue: idInput && idInput.value,
          eventVariantId: variant && variant.id,
          resolvedVariantId: variantId,
          owned,
          metafieldMatch,
          ownedIds: [...this.ownedVariantIds],
        });
      }

      if (!variantId) return;

      // Only act on variants that belong to this gallery's product. A foreign id
      // (a transient event from another product during a combined-listing swap, or
      // a stale gallery still in the DOM) must not blank the authoritative server
      // render — leave it for the correct gallery to handle.
      if (this.ownedVariantIds.size && !this.ownedVariantIds.has(variantId)) return;

      // Record what we're applying so the id-input observer doesn't re-fire for it.
      this.lastAppliedVariantId = variantId;

      const useMetafield = metafieldMatch;

      // Metafield and native visibility are BOTH controlled by CSS classes with
      // !important, so nothing (the theme's grouping, inline display writes, etc.)
      // can override this component's decision. Clear any inline display the theme
      // may have set so only the classes matter.
      const markMetafield = (items) => {
        items.forEach((item) => {
          item.style.display = '';
          item.classList.toggle(
            'is-metafield-visible',
            useMetafield && item.dataset.variantId === variantId
          );
        });
      };
      markMetafield(this.viewer.querySelectorAll('.media-viewer__item--metafield'));
      if (this.thumbs) {
        markMetafield(this.thumbs.querySelectorAll('.media-thumbs__item--metafield'));
      }

      // Force-hide native (parent) media while metafield images are active.
      const markNative = (items) => {
        items.forEach((item) => {
          item.style.display = '';
          item.classList.toggle('is-native-hidden', useMetafield);
        });
      };
      markNative(this.viewer.querySelectorAll('.media-viewer__item[data-native-media]'));
      if (this.thumbs) {
        markNative(this.thumbs.querySelectorAll('.media-thumbs__item[data-native-media]'));
        this.thumbsItems = this.thumbs.querySelectorAll('.media-thumbs__item');
      }

      this.metafieldActive = useMetafield;

      if (useMetafield) {
        this.setVisibleItems();
        if (this.zoomLinks && this.dataset.zoomTrigger === 'hover') this.initZoom();
        if (this.visibleItems[0]) this.customSetActiveMedia(this.visibleItems[0], true);
        return;
      }

      // No metafield images for this variant: defer to the parent product's
      // normal behaviour (native media grouping). Metafield items remain hidden
      // via CSS, immune to grouping's "show all" fallback.
      this.setVisibleItems();
      if (this.mediaGroupingEnabled) {
        this.currentGroup = null;
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
        this.setVisibleItems();
      }
      if (this.zoomLinks && this.dataset.zoomTrigger === 'hover') this.initZoom();

      let target = null;
      if (variant && variant.featured_media) {
        target = this.viewer.querySelector(
          `.media-viewer__item[data-native-media][data-media-id="${variant.featured_media.id}"]`
        );
      }
      if (!target || target.clientWidth === 0) target = this.visibleItems[0];
      this.customSetActiveMedia(target, true);
    }

    /**
     * Gets the media group from currently selected variant options.
     * @returns {?object}
     */
    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');
      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];
        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }
        return selector.querySelector('input:checked').value;
      }
      return null;
    }

    /**
     * Gets the variant media associations for a product.
     * @returns {?object}
     */
    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    /**
     * Gets an object mapping media to groups, and the reverse
     * @returns {?object}
     */
    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        // set up grouping
        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;
        this.viewerItems = this.querySelectorAll('.media-viewer__item');
        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }
          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }
            const groupItem = { main: item };
            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }
            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        // add helper
        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);
          for (let i = 0; i < groups; i += 1) {
            const group = groups[i];
            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j] === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }
          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    /**
     * Show only images associated to the current variant
     * @param {string} groupName - optional - Group to show (uses this.currentItem if empty)
     */
    setActiveMediaGroup(groupName) {
      // For products with variant metafield images, applyVariantMedia is the sole
      // controller of gallery visibility. Native media grouping must never run —
      // it would reveal its own native group on top of the metafield images.
      if (this.hasMetafieldMedia) {
        if (window.MF_DEBUG) {
          (window.MF_LOG = window.MF_LOG || []).push({
            t: Date.now(), event: 'setActiveMediaGroup BLOCKED', groupName, stack: new Error().stack,
          });
          console.debug('[mf] setActiveMediaGroup BLOCKED (metafield product)', { groupName });
        }
        return;
      }

      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;
          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });
          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;
          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';
            if (item.thumb) {
              item.thumb.style.display = '';
            }
            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });
          this.setVisibleItems();

          // If current item is not in this group, set it as the active item
          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          // Handle single images on stacked view
          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });
        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    /**
     * Initialises the media gallery slider and associated controls.
     */
    initGallery() {
      this.setVisibleItems();
      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        // Eager load the slider images for smooth UX
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));
      if (this.controls) this.controls.addEventListener('click', this.handleNavClick.bind(this));
      if (this.thumbs) this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    /**
     * Initialized the zoom on hover for desktop
     */
    initZoom() {
      this.zoomLinks.forEach((el) => {
        const zoomWidth = Number(el.querySelector('.zoom-image').dataset.originalWidth || 0);
        const imageWidth = el.querySelector('.product-image').getBoundingClientRect().width;
        if (theme.mediaMatches.md && ((zoomWidth - 75) > (imageWidth))) {
          el.addEventListener('mousemove', this.zoomEventListener);
          el.classList.remove('pointer-events-none');
        } else {
          el.removeEventListener('mousemove', this.zoomEventListener);
          el.classList.add('pointer-events-none');
        }
      });
    }

    /**
     * Handles mouse move over a zoomable image
     * @param {?object} evt - Event object.
     */
    handleZoomMouseMove(evt) {
      if (this.hasAttribute('data-zoom-enabled') && !this.dataset.zoomTrigger && !evt.target.closest('.gallery-zoom-modal')) {
        return;
      }

      const hoverElem = evt.currentTarget;
      const zoomImage = hoverElem.querySelector('.js-zoom-image');

      // Download the zoom image if necessary
      if (zoomImage.dataset.src) {
        this.loadingSpinner.classList.remove('loading-spinner--out');

        const img = new Image();
        img.src = zoomImage.dataset.src;
        img.onload = () => {
          zoomImage.src = img.src;
          hoverElem.classList.remove('media--zoom-not-loaded');
          this.loadingSpinner.classList.add('loading-spinner--out');
        };
        zoomImage.removeAttribute('data-src');
      }

      try {
        const offsetX = evt.offsetX ? evt.offsetX : evt.touches[0].pageX;
        const offsetY = evt.offsetY ? evt.offsetY : evt.touches[0].pageY;
        const x = (offsetX / zoomImage.offsetWidth) * 100;
        const y = (offsetY / zoomImage.offsetHeight) * 100;
        zoomImage.style.objectPosition = `${x}% ${y}%`;
      } catch (err) {
        // Fail silently
      }
    }

    /**
     * Handles 'scroll' events on the main media container.
     */
    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        // If scroll wasn't caused by a resize event, update the active media.
        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    /**
     * Handles 'click' events on the controls container.
     * @param {object} evt - Event object.
     */
    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    /**
     * Handles 'click' events on the thumbnails container.
     * @param {object} evt - Event object.
     */
    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    /**
     * Handles debounced 'resize' events on the window.
     */
    handleResize() {
      // Reset distance from leading edge of one slide to the next.
      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    /**
     * Stub for variant-picker calls. Listening to change event instead.
     */
    // eslint-disable-next-line class-methods-use-this
    setActiveMedia() {}

    /**
     * Sets the active media item.
     * @param {Element} mediaItem - Media element to set as active.
     * @param {boolean} [scrollToItem=true] - Scroll to the active media item.
     */
    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem || mediaItem === this.currentItem) return;
      window.pauseAllMedia(this);
      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        // Update the active class and scroll to the active media
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');
          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          // If the element is far enough away to scroll to it
          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }
        return;
      }

      if (scrollToItem) this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      if (this.thumbs) this.setActiveThumb();

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    /**
     * Sets the active thumbnail.
     */
    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );
      if (!this.currentThumb) return;
      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    /**
     * Creates an array of the visible media items.
     */
    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');
      this.visibleItems = Array.from(this.viewerItems).filter((el) => el.clientWidth > 0);
      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    /**
     * Ensures a thumbnail is in the visible area of the slider.
     * @param {Element} thumb - Thumb item element.
     */
    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    /**
     * Updates the media gallery status.
     * @param {Element} mediaItem - Active media element.
     * @param {number} index - Active media index.
     */
    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');
      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    /**
     * Loads the deferred media for the active item.
     * @param {Element} mediaItem - Active media element.
     */
    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();
      const deferredMedia = mediaItem.querySelector('deferred-media');
      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}
